<script setup>
import { ref } from "vue";
import { userTokenStore } from "@/store/store.js";
import { genFileId } from "element-plus";
import {useSnowflakeHook} from "@/hooks/snowflake.js";
import {getSignatureUrl} from "@/api/upload/upload.js";
const { generateId } = useSnowflakeHook();
import axios from 'axios';

const props = defineProps({
  // 文件最大上传数量
  limit: {
    type: Number,
    default: 5,
  },
  // 图片宽度
  width: {
    type: Number,
    default: 900,
  },
  // 图片高度
  height: {
    type: Number,
    default: 400,
  },
  // 图片大小：MB
  fileSize: {
    type: Number,
    default: 1,
  },
  // 文件格式
  fileType: {
    type: Array,
    default: () => ["jpg", "jpeg", "png", "JPG", "JPEG", "PNG"],
  },
  // 提示出现位置，默认情况下是document.body，可以参考ElementPlus的Message组件文档
  tipsAppendTo: {
    type: String,
    default: "document.body",
  },
  // 外部索引，主要用于成功事件携带外部索引值，提供同个页面多个上传组件的索引值给外部赋值使用
  index: {
    type: Number,
    default: null,
  },
  // 是否显示必填
  isRequired: {
    type: Boolean,
    default: false,
  },
  // 替换模式：仅支持limit===1，其余limit会出现bug
  isReplaceMode: {
    type: Boolean,
    default: false,
  },

  showFileList: {
    type: Boolean,
    default: true,
  },
  baseUrl: {
    type: String,
    default: "/upload/uploadImg",
  },

  cache_type: {
    type: Boolean,
    default: true,
  },
  // 阿里oss预上传地址枚举
  aliOssPath: {
    required: true,
    type: String,
  },
  coverName: {
    type: String,
    default: null,
  }
});
// 事件
const emit = defineEmits(["success", "change", "remove", "progress", "fail"]);

/*文件上传请求配置*/
// 请求基地址
const baseUrl = import.meta.env.VITE_API_URL + props.baseUrl;
// token
const token = userTokenStore().token;
// 请求头携带信息
const headers = {
  Authorization: token.tokenValue,
  "Login-Type": token.loginType,
};
// 请求体携带信息
const data = {
  // 1临时文件 2永久文件
  uploadDownloadType: 2,
};

/*组件功能定义*/
// 是否有新上传文件（反显时配合提交功能使用）
const isChange = ref(false);
// 用于展示的文件列表
const fileList = ref([]);
// 用于对话框展示的大图地址
const dialogImageUrl = ref("");
// 展示对话框开启状态
const dialogVisible = ref(false);
// 组件锚点
const fileUpload = ref();
// 展示大图
const handleOnPreview = (file) => {
  console.log("run in handleOnPreview file", file);
  dialogImageUrl.value = file.url;
  dialogVisible.value = true;
};
// 删除展示的文件
const handleOnRemove = (file) => {
  console.log("过滤之前", successUrl.value);
  successUrl.value = [];
  fileList.value = fileList.value.filter((item) => {
    if (item.uid !== file.uid) successUrl.value.push(item.url);
    return item.uid !== file.uid;
  });
  console.log("移除了文件", successUrl.value);
  emit("remove", successUrl.value, props.index);
  successLimit.value--;
};
// 文件上传成功
const handleOnSuccess = (file) => {
  try {
    successUrl.value.push(file.data);
    console.log("successUrl.value: ", successUrl.value);
    successLimit.value++;
    if (props.isReplaceMode) {
      if (successLimit.value !== fileList.value.length) {
        console.log("上传成功数组与组件缓存图片张数不符，清空上传成功数组重新赋值");
        successUrl.value = [];
        successLimit.value = 0;
        successUrl.value.push(file.data);
        successLimit.value++;
      }
      emit("success", successUrl.value, props.index);
    } else {
      if (successLimit.value === fileList.value.length) {
        emit("success", successUrl.value, props.index);
        // 如果cache_type为false，每次上传成功后清空缓存
        if (!props.cache_type) {
          successUrl.value = [];
          fileList.value = [];
          successLimit.value = 0;
          console.log("successUrl.value: ", successUrl.value);
        }
      }
    }
  } catch (e) {
    console.log(e);
    ElMessage({
      message: file.msg ? file.msg : "未知错误",
      type: "error",
      appendTo: props.tipsAppendTo,
    });
  }
};
// 文件上传失败
const handleOnError = () => {
  console.log("run in handleOnError");
  ElMessage({
    message: "上传失败，请重试",
    type: "error",
    appendTo: props.tipsAppendTo,
  });
  emit("remove", false);
};
// 文件上传中
const handleProgress = () => {
  console.log("run in handleProgress");
  emit("progress");
};
// 选择了上传文件，但未请求上传接口
const handleOnChange = async (file) => {
  console.log("run in change");
  console.log(file);
  const input = fileUpload.value?.$el?.querySelector('input[type="file"]')
  const files = input?.files
  console.log("files", files)
  if (files.length > 30) {
    ElMessage.error("单次上传最多选择30张图片！")

    fileList.value = fileList.value.filter(f => f.status === "success" && !f.url.startsWith("blob:"))
    successUrl.value = successUrl.value.filter(url => !url.startsWith("blob:"))
    successLimit.value = successUrl.value.length
    return
  }
  isChange.value = true;
  const isPassFormat = await checkFileFormat(file);
  if (!isPassFormat) return
  const res = await uploadFile(file)
  console.log("res", res)
  if (res.code === 0) {
    let param = {
      ...res.fileInfo,
      data: res.fileUrl
    }
    handleOnSuccess(param)
  } else {
    ElMessage.error("文件上传失败")
  }
  // emit("change", fileList.value.length);
};
// 文件上传格式、大小错误
const handleOnExceed = (files) => {
  if (props.isReplaceMode) {
    fileUpload.value.clearFiles();
    const file = files[0];
    file.uid = genFileId();
    fileUpload.value.handleStart(file);
    console.log("file", file);
    // fileUpload.value.submit();
  } else {
    ElMessage({
      message: `上传文件数量不能超过 ${props.limit} 个!`,
      type: "error",
      appendTo: props.tipsAppendTo,
    });
  }
};
// 校验文件类型和文件大小
const checkFileFormat = async (file) => {
  // 校检文件类型
  if (props.fileType) {
    const fileName = file.name.split(".");
    const fileExt = fileName[fileName.length - 1];
    const isTypeOk = props.fileType.indexOf(fileExt) >= 0;
    if (!isTypeOk) {
      ElMessage({
        message: `文件格式不正确, 请上传${props.fileType.join("/")}格式文件!`,
        type: "error",
        appendTo: props.tipsAppendTo,
      });
      const index = fileList.value.indexOf(file.uid);
      fileList.value.splice(index, 1);
      return false;
    }
  }
  // 校检文件大小
  if (props.fileSize) {
    const isLt = file.size / 1024 / 1024 < props.fileSize;
    if (!isLt) {
      ElMessage({
        message: `上传文件大小不能超过 ${props.fileSize} MB!`,
        type: "error",
        appendTo: props.tipsAppendTo,
      });
      const index = fileList.value.indexOf(file.uid);
      fileList.value.splice(index, 1);
      return false;
    }
  }
  return true;
};
// 文件上传
const handleUpload = () => {
  fileUpload.value.submit();
};

/*反显功能*/
// 成功上传的文件数量（反显时配合提交功能使用）
const successLimit = ref(0);
// 成功上传的文件地址（反显时配合提交功能使用）
const successUrl = ref([]);
// 反显图片
const handleReview = (reviewList) => {
  console.log("handleReview", reviewList);
  fileList.value = [];
  reviewList.forEach((item) => {
    fileList.value.push({ url: item });
    successUrl.value.push(item);
  });
  successLimit.value = fileList.value.length;
};

const disabled = ref(false);
const handleRemove = (file, index) => {
  console.log(file);
  fileList.value.splice(index, 1);
};
const handlePictureCardPreview = (file) => {
  dialogImageUrl.value = file.url;
  dialogVisible.value = true;
};

// 处理更换图片
const handleReplace = (reviewList) => {
  fileUpload.value.$el.querySelector("input").click();
};
// 使用预上传地址上传文件
const uploadFile = async (file) => {
  const urlParam = await getFileUploadUrl(file)
  if (urlParam.code === -1) return ElMessage.error("上传失败")
  const urlObj = new URL(urlParam.signedUrl);
  const fileUrl = urlObj.origin + urlObj.pathname;

  try {
    const response = await axios.put(urlParam.signedUrl, file.raw, {
      headers: { 'Content-Type': 'application/octet-stream' }
    });

    if (response.status === 200) {
      return { code: 0, fileUrl: [fileUrl], fileInfo: file };
    } else {
      return { code: -1, fileUrl: null };
    }

  } catch (err) {
    console.error(err);
    return { code: -1, fileUrl: null };
  }
}
// 获取文件预上传地址
const getFileUploadUrl = async (file) => {
  try {
    const id = generateId()
    const rawFile = file.raw
    // 获取文件后缀
    const ext = rawFile.name.includes('.') ? rawFile.name.substring(rawFile.name.lastIndexOf('.')) : '.bin';
    let param = {
      uploadDownloadTypeEnum: "PERMANENT",
      aliOssImgUploadPathEnum: props.aliOssPath,
      fileName: props.aliOssPath + id + ext
    }
    const res = await getSignatureUrl(param)
    console.log("res", res)
    return {code: 0, signedUrl: res}
  } catch (e) {
    console.log(e)
    return {code: -1, signedUrl: null}
  }
}

// 暴露给外部使用的变量以及函数
defineExpose({
  handleUpload,
  handleReview,
  fileList,
  successUrl,
  isChange,
});
</script>

<template>
  <transition name="el-fade-in-linear">
    <div v-if="isReplaceMode">
      <div
        class="el-upload-list el-upload-list--picture-card"
        v-if="fileList.length > 0"
      >
        <div
          class="el-upload-list__item is-success"
          style="border-radius: 15px"
          v-for="(item, index) in fileList"
          :key="`fileList${index}`"
        >
          <label class="success-icon">
            <i class="el-icon el-icon--upload-success el-icon--check icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024">
                <path
                  fill="currentColor"
                  d="M406.656 706.944 195.84 496.256a32 32 0 1 0-45.248 45.248l256 256 512-512a32 32 0 0 0-45.248-45.248L406.592 706.944z"
                ></path>
              </svg>
            </i>
          </label>
          <img class="el-upload-list__item-thumbnail" :src="item.url" alt="" />
          <div class="mask cursor-pointer flex_center" @click="handleReplace()">
            <div>更换图片</div>
          </div>
        </div>
      </div>
    </div>
  </transition>

  <el-upload
    style="text-align: left; border-radius: 15px"
    v-model:file-list="fileList"
    :action="baseUrl"
    :multiple="false"
    :on-preview="handleOnPreview"
    :on-remove="handleOnRemove"
    :on-success="handleOnSuccess"
    :on-error="handleOnError"
    :on-progress="handleProgress"
    :on-exceed="handleOnExceed"
    :before-upload="checkFileFormat"
    :show-file-list="showFileList"
    :on-change="handleOnChange"
    list-type="picture-card"
    :limit="props.limit"
    :headers="headers"
    :data="data"
    ref="fileUpload"
    v-show="fileList.length < props.limit"
    :auto-upload="false"
  >
    <div>
      <div class="flex_center flex_warp mg_b_1">
        <img
            style="width: 62px; height: 53px;"
            src="https://yttec-bucket.oss-cn-guangzhou.aliyuncs.com/resources/newUi/upload/upload/unUpload.svg"
        />
      </div>
      <div class="coverName" v-show="props.coverName">{{ props.coverName }}</div>
    </div>
  </el-upload>

  <div class="el-upload__tip flex" style="line-height: 22px;margin-top: 20px;margin-left: -10px;">
    <div>
      <span style="color: #dc4c2f; margin-right: 5px" v-if="props.isRequired">*</span>
    </div>
    <div>
      <div>
        <span >请上传{{ props.limit }}张{{ props.width }} * {{ props.height }}px的图片</span>
      </div>
      <div>图片大小不超过{{ props.fileSize }}MB</div>
    </div>
  </div>

  <el-dialog v-model="dialogVisible">
    <img width="100%" :src="dialogImageUrl" alt="Preview Image" />
  </el-dialog>
</template>

<style scoped>
::v-deep(.el-upload:focus,),
::v-deep(.el-upload:focus .el-upload-dragger),
::v-deep(.el-upload--picture-card:hover) {
  border-color: #272539;
  color: #272539;
}
::v-deep(.el-upload--picture-card) {
  border-radius: 15px;
}

.mask {
  width: 100%;
  height: 23%;
  position: absolute;
  bottom: 0;
  left: 0;
  background-color: #aea5d9;
  text-align: center;
  font-weight: 500;
  font-size: 16px;
  color: #ffffff;
  box-sizing: border-box;
}
.success-icon {
  position: absolute;
  background: var(--el-color-success);
  height: 24px;
  right: -15px;
  text-align: center;
  top: -6px;
  transform: rotate(45deg);
  width: 40px;
  line-height: 10px;
  display: flex;
  justify-content: center;
  .icon {
    position: absolute;
    bottom: 0;
    transform: rotate(320deg);
  }
}
.coverName {
  color: #d2d3d6;
  font-size: 18px;
  font-weight: 800;
}
</style>
